import type { TranscriptTextToken, TranscriptToken } from '@src/editor/types';
import { KaraokeClip } from '../models/karaoke/KaraokeClip';
import { DEFAULT_MINIMUM_TOKEN_DURATION } from './transcriptValidations';

// Segmentation constants (from KaraokeSegmenter)
const MAX_SEGMENT_CHAR_LENGTH = 36;
const PAUSE_DURATION_MS = 0.6;
const NATURAL_BEAT_DURATION_MS = 0.8;
const ORPHANED_WORDS_COUNT = 2;

/**
 * Represents a karaoke segment with tokens and timing information
 */
export type KaraokeSegment = {
  tokens: TranscriptToken[];
  startTime: number | null;
  endTime: number | null;
};

/**
 * Segment transcript tokens into KaraokeSegments
 * Based on KaraokeProducer.assembleKaraokeClips and KaraokeSegmenter logic
 *
 * Note: activeTranscriptTokens have removed/cut tokens already filtered out,
 * but muted tokens are included. We keep muted tokens in segments but track them
 * so they don't appear in karaoke display.
 */
export function segmentTokens(tokens: TranscriptToken[]): KaraokeSegment[] {
  const segments: KaraokeSegment[] = [];
  let currentClipTokens: TranscriptToken[] = [];
  let currentClipStartTime: number | null = null;
  let currentClipEndTime: number | null = null;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // Initialize clip times with first text token (including muted)
    if (token.type === 'text' && currentClipStartTime === null) {
      currentClipStartTime = token.ts;
      currentClipEndTime = token.end_ts;
    }

    // Add token to current clip (including muted tokens)
    currentClipTokens.push(token);

    // Update end time if this is a text token
    if (token.type === 'text' && token.end_ts !== null) {
      currentClipEndTime = token.end_ts;
    }

    // Check if we should end the current segment
    if (shouldEndSegment(currentClipTokens, tokens, i)) {
      // Append any trailing punctuation to the current clip
      // This matches KaraokeSegmenter behavior
      let nextToken = tokens[i + 1];
      while (nextToken && nextToken.type === 'punct') {
        currentClipTokens.push(nextToken);
        i++; // Advance index
        nextToken = tokens[i + 1];
      }

      segments.push({
        tokens: currentClipTokens,
        startTime: currentClipStartTime,
        endTime: currentClipEndTime,
      });

      // Reset for next clip
      currentClipTokens = [];
      currentClipStartTime = null;
      currentClipEndTime = null;
    }
  }

  // Add remaining tokens as final clip
  if (currentClipTokens.length > 0) {
    segments.push({
      tokens: currentClipTokens,
      startTime: currentClipStartTime,
      endTime: currentClipEndTime,
    });
  }

  // Post-process: squash orphaned segments (segments with 2 or fewer words)
  return segments;
}

/**
 * Check if token is muted (should be hidden but still included in clip)
 * Muted tokens are tracked separately
 */
function isTokenMuted(token: TranscriptToken): boolean {
  void token;
  // None muted initially
  // TODO: check for filler words if config hide fillers is enabled
  return false;
}

/**
 * Determine if we should end the current segment
 * Based on KaraokeSegmenter.shouldEndSegment logic
 *
 * Note: This is called AFTER the token at currentIndex has been added to currentTokens,
 * so if we return true, the token WITH the break mark will be included in the current clip.
 */
function shouldEndSegment(
  currentTokens: TranscriptToken[],
  allTokens: TranscriptToken[],
  currentIndex: number,
): boolean {
  const currentToken = allTokens[currentIndex];

  // Check if max character length reached
  const currentText = getTextFromTokens(currentTokens);
  if (currentText.length >= MAX_SEGMENT_CHAR_LENGTH) {
    return true;
  }

  // Check for significant punctuation (., !, ?)
  if (
    currentToken.type === 'punct' &&
    currentToken.value &&
    ['.', '!', '?'].includes(currentToken.value)
  ) {
    return true;
  }

  // Check for pause or time gap
  if (currentToken.type === 'text') {
    const nextTextToken = findNextTextToken(allTokens, currentIndex);
    if (nextTextToken) {
      const currentEndTs = currentToken.end_ts || currentToken.ts;
      const gap = nextTextToken.ts! - currentEndTs!;

      // Pause detection (600ms)
      if (gap > PAUSE_DURATION_MS) {
        return true;
      }

      // Natural beat detection (800ms)
      if (gap > NATURAL_BEAT_DURATION_MS) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Find the next text token from current position
 */
function findNextTextToken(
  tokens: TranscriptToken[],
  fromIndex: number,
): TranscriptTextToken | null {
  for (let i = fromIndex + 1; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'text') {
      return token;
    }
  }
  return null;
}

/**
 * Get combined text from token array (excluding muted tokens)
 * Used for calculating line length for segmentation
 */
function getTextFromTokens(tokens: TranscriptToken[]): string {
  return tokens
    .filter(t => !isTokenMuted(t))
    .map(t => t.value || '')
    .join('');
}

/**
 * Squash orphaned segments (similar to KaraokeSegmenter.squashOrphanedSegments)
 * Merges segments with 2 or fewer visible (non-muted) words into the previous segment
 */
export function squashOrphanedSegments(clips: KaraokeClip[]): KaraokeClip[] {
  for (let i = 1; i < clips.length; i++) {
    const currentClip = clips[i];
    const prevClip = clips[i - 1];

    // Count visible (non-muted) text tokens in current clip
    const visibleTextTokenCount = currentClip.transcriptTokens.filter(
      t => t.type === 'text' && !isTokenMuted(t),
    ).length;

    if (visibleTextTokenCount <= ORPHANED_WORDS_COUNT) {
      // Check if we should merge (not across significant breaks)
      const lastPrevToken =
        prevClip.transcriptTokens[prevClip.transcriptTokens.length - 1];
      const firstCurrentToken = currentClip.transcriptTokens[0];

      // Don't merge across significant punctuation or large gaps
      const shouldMerge = shouldMergeClips(lastPrevToken, firstCurrentToken);

      if (shouldMerge) {
        // Merge current clip into previous
        prevClip.transcriptTokens.push(...currentClip.transcriptTokens);
        // Merge hidden token IDs
        prevClip.hiddenTranscriptTokenIds.push(
          ...currentClip.hiddenTranscriptTokenIds,
        );
        clips.splice(i, 1);
        i--; // Adjust index after removal
      }
    }
  }

  return clips;
}

/**
 * Check if two segments should be merged
 */
function shouldMergeClips(
  lastTokenOfPrev: TranscriptToken,
  firstTokenOfCurrent: TranscriptToken,
): boolean {
  // Don't merge across significant punctuation
  if (
    lastTokenOfPrev.type === 'punct' &&
    lastTokenOfPrev.value &&
    ['.', '!', '?'].includes(lastTokenOfPrev.value)
  ) {
    return false;
  }

  // Don't merge across large time gaps
  if (lastTokenOfPrev.type === 'text' && firstTokenOfCurrent.type === 'text') {
    const gap =
      firstTokenOfCurrent.ts! - (lastTokenOfPrev.end_ts || lastTokenOfPrev.ts)!;
    if (gap > PAUSE_DURATION_MS) {
      return false;
    }
  }

  return true;
}

/**
 * Resolves overlaps between karaoke clips by adjusting offsets.
 * Strategy:
 * 1. For each overlapping pair, prefer adjusting clips with expanded offsets
 * 2. Reduce expanded offsets toward zero (shrinking clip back to natural duration)
 * 3. Only shrink clips beyond natural duration if reducing expanded offsets doesn't resolve overlap
 *
 * Note:
 * - Negative startSecondsOffset means clip starts earlier (expanded backward)
 * - Positive endSecondsOffset means clip ends later (expanded forward)
 */
export function resolveOverlaps(clips: KaraokeClip[]): void {
  for (let i = 0; i < clips.length - 1; i++) {
    const currentClip = clips[i];
    const nextClip = clips[i + 1];

    const currentEnd = currentClip.getKaraokeEndTime();
    const nextStart = nextClip.getKaraokeStartTime();

    if (currentEnd === null || nextStart === null) {
      continue;
    }

    // Check for overlap
    if (currentEnd > nextStart) {
      const overlap = currentEnd - nextStart;
      console.warn(
        `Detected overlap of ${overlap}s between clips ${currentClip.id} and ${nextClip.id}`,
      );

      resolveOverlapBetweenClips(currentClip, nextClip, overlap);
    }
  }
}

/**
 * Resolves overlap between two adjacent clips
 */
function resolveOverlapBetweenClips(
  clip1: KaraokeClip,
  clip2: KaraokeClip,
  overlap: number,
): void {
  const clip1EndOffset = clip1.endSecondsOffset || 0;
  const clip2StartOffset = clip2.startSecondsOffset || 0;

  // Strategy 1: Try to reduce expanded offsets toward zero
  // - clip1 with positive endSecondsOffset is expanded forward
  // - clip2 with negative startSecondsOffset is expanded backward
  const clip1ExpandedForward = Math.max(0, clip1EndOffset);
  const clip2ExpandedBackward = Math.abs(Math.min(0, clip2StartOffset));

  if (clip1ExpandedForward > 0 || clip2ExpandedBackward > 0) {
    const totalCanReduce = clip1ExpandedForward + clip2ExpandedBackward;

    if (totalCanReduce >= overlap) {
      // We can fully resolve by reducing expanded portions
      const clip1Reduction = Math.min(clip1ExpandedForward, overlap);
      const remainingOverlap = overlap - clip1Reduction;
      const clip2Reduction = Math.min(clip2ExpandedBackward, remainingOverlap);

      if (clip1Reduction > 0) {
        const newEndOffset = clip1EndOffset - clip1Reduction;
        clip1.endSecondsOffset = newEndOffset;
        console.log(
          `Resolved overlap by reducing clip ${clip1.id} endSecondsOffset from ${clip1EndOffset} to ${newEndOffset}`,
        );
      }

      if (clip2Reduction > 0) {
        const newStartOffset = clip2StartOffset + clip2Reduction;
        clip2.startSecondsOffset = newStartOffset;
        console.log(
          `Resolved overlap by reducing clip ${clip2.id} startSecondsOffset from ${clip2StartOffset} to ${newStartOffset}`,
        );
      }

      if (clip1Reduction + clip2Reduction >= overlap) {
        return;
      }
    } else {
      // Partially resolved, apply all available reductions
      if (clip1ExpandedForward > 0) {
        clip1.endSecondsOffset = 0;
        console.log(
          `Partially resolved overlap by resetting clip ${clip1.id} endSecondsOffset to 0`,
        );
      }
      if (clip2ExpandedBackward > 0) {
        clip2.startSecondsOffset = 0;
        console.log(
          `Partially resolved overlap by resetting clip ${clip2.id} startSecondsOffset to 0`,
        );
      }
      overlap = overlap - totalCanReduce;
    }
  }

  // Strategy 2: Shrink clips beyond their natural duration (inward)
  const clip1MinDuration = DEFAULT_MINIMUM_TOKEN_DURATION;
  const clip2MinDuration = DEFAULT_MINIMUM_TOKEN_DURATION;

  const clip1CurrentDuration = clip1.getKaraokeDuration();
  const clip2CurrentDuration = clip2.getKaraokeDuration();

  if (clip1CurrentDuration === null || clip2CurrentDuration === null) {
    console.error('Cannot resolve overlap: invalid clip durations');
    return;
  }

  // Calculate how much we can shrink each clip
  const clip1CanShrink = Math.max(0, clip1CurrentDuration - clip1MinDuration);
  const clip2CanShrink = Math.max(0, clip2CurrentDuration - clip2MinDuration);

  if (clip1CanShrink + clip2CanShrink < overlap) {
    console.error(
      `Cannot fully resolve overlap: clips cannot shrink enough (need ${overlap}s, can shrink ${
        clip1CanShrink + clip2CanShrink
      }s)`,
    );
    // Still apply maximum possible shrinkage
  }

  // Distribute shrinkage proportionally
  let clip1Shrink = 0;
  let clip2Shrink = 0;
  const halfOverlap = overlap / 2;

  if (clip1CanShrink >= halfOverlap && clip2CanShrink >= halfOverlap) {
    // Both can accommodate half the overlap
    clip1Shrink = halfOverlap;
    clip2Shrink = halfOverlap;
  } else if (clip1CanShrink < halfOverlap) {
    // Clip1 can't shrink enough, clip2 takes more
    clip1Shrink = clip1CanShrink;
    clip2Shrink = Math.min(clip2CanShrink, overlap - clip1Shrink);
  } else {
    // Clip2 can't shrink enough, clip1 takes more
    clip2Shrink = clip2CanShrink;
    clip1Shrink = Math.min(clip1CanShrink, overlap - clip2Shrink);
  }

  if (clip1Shrink > 0) {
    const newEndOffset = (clip1.endSecondsOffset || 0) - clip1Shrink;
    clip1.endSecondsOffset = newEndOffset;
    console.log(
      `Resolved overlap by shrinking clip ${clip1.id} endSecondsOffset to ${newEndOffset} (reduced by ${clip1Shrink}s)`,
    );
  }

  if (clip2Shrink > 0) {
    const newStartOffset = (clip2.startSecondsOffset || 0) + clip2Shrink;
    clip2.startSecondsOffset = newStartOffset;
    console.log(
      `Resolved overlap by shrinking clip ${clip2.id} startSecondsOffset to ${newStartOffset} (increased by ${clip2Shrink}s)`,
    );
  }
}
